import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Location, DatePipe } from '@angular/common';
import { filter, map, switchMap } from "rxjs/operators";
import { concat } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard, MatCardHeader, MatCardAvatar, MatCardTitle, MatCardSubtitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { PersonDto, PersonsApiService, TeamTransferTokensApiService } from '../../../../../api/generated';
import { notNullOrUndefined, loadingObservable } from '../../../../../shared/utils';
import { ConfirmComponent, ConfirmResult } from '../../components/confirm/confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-person-details',
  templateUrl: './person-details.component.html',
  styleUrls: ['./person-details.component.scss'],
  standalone: true,
  imports: [MatCard, MatCardHeader, MatCardAvatar, MatCardTitle, MatCardSubtitle, MatCardContent, MatProgressSpinner, MatCardActions, MatButton, RouterLink, DatePipe, TranslateModule],
})
export class PersonDetailsComponent {
  isPersonCreated: boolean = false;
  person: PersonDto | null = null;
  loading: boolean = false;
  disableTokenButton: boolean = false;

  constructor(
    private personService: PersonsApiService,
    private tokenService: TeamTransferTokensApiService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.route.params
      .pipe(
        map(params => params['id'] as string | null),
        filter(notNullOrUndefined),
        switchMap(guid => loadingObservable(this.personService.getApiV10Persons(guid))),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loading = response.loading;
          } else {
            this.person = response.person;
          }
        }
      });

    this.route.params
      .pipe(
        map(params => params['isPersonCreated'] as string | null),
        filter(notNullOrUndefined),
        map(isPersonCreated => isPersonCreated === 'true'),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (isPersonCreated) => {
          this.isPersonCreated = isPersonCreated;
        }
      });
  }

  // Als alle tokens inactief zijn is de deactiveer unieke token 'disabled'
  isAllTokensInactive(): boolean {
    return this.person?.tokens.every(token => !token.isActive) ?? false;
  }

  createUniqueNewToken() {
    if (this.person === null) {
      //todo: feedback to user
      return;
    }

    concat(
      loadingObservable(this.tokenService.postApiV10TeamTransferTokens({ personGuid: this.person.guid })),
      loadingObservable(this.personService.getApiV10Persons(this.person.guid))
    )
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loading = response.loading;
          }

          if ('person' in response) {
            this.person = response.person;
          }
        }
      });
  }

  async deactivateUniqueToken() {
    if (this.person === null) {
      //todo: feedback to user
      return;
    }

    concat(
      loadingObservable(this.tokenService.putApiV10TeamTransferTokensDeactivate({ personGuid: this.person.guid })),
      loadingObservable(this.personService.getApiV10Persons(this.person.guid))
    )
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loading = response.loading;
          }

          if ('person' in response) {
            this.person = response.person;
          }
        }
      });
  }

  async openConfirmDialog() {
    this.translate.get(['emailPopup.title', 'emailPopup.content']).subscribe(translations => {
      const dialogRef = this.dialog.open(ConfirmComponent, {
        width: '600px',
        data: {
          title: translations['emailPopup.title'],
          content: translations['emailPopup.content']
        },
      });

      dialogRef.afterClosed().subscribe(async (result: ConfirmResult) => {
        if (result.confirmed) {
          await this.sendToken();
        }
      });
    });
  }

  sendToken() {
    this.disableTokenButton = true;
    this.openSnackBar('emailSend');
    this.personService.postApiV10PersonsSendEmailWithToken({
      guid: this.person!.guid
    }).subscribe({
      next: (response) => {
        this.disableTokenButton = false;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }


  openSnackBar(message: string) {
    this.translate.get(message).subscribe((message: string) => {
      this.translate.get('close').subscribe((close: string) => {
        this.snackBar.open(message, close, { duration: 5000, horizontalPosition: 'end', verticalPosition: 'top' });
      });
    });
  }
}
